import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from '@/utils/axiosConfig';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { FolderOpen, FileCheck, FileClock, Receipt, Users, TrendingUp, Calendar as CalendarIcon, Plus, Clock, MapPin, Trash2, CheckCircle, XCircle, ExternalLink, Briefcase, FileText, User, Eye, Archive, ListTodo, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import Calendar from 'react-calendar';

const Dashboard = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDocsCount, setPendingDocsCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [fileStatusTab, setFileStatusTab] = useState('actif');
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    participant_ids: [],
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '09:00',
    duration: 60,
    location: '',
    meeting_type: 'employee'
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    // ✅ AbortController pour cleanup automatique
    const abortController = new AbortController();
    let isMounted = true;

    const loadAllData = async () => {
      try {
        const signal = abortController.signal;
        const mountedRef = { current: isMounted };

        // Execute all data fetching in parallel for better performance
        const promises = [
          fetchStats(signal, mountedRef),
          fetchMeetings(signal, mountedRef),
          fetchFiles(signal, mountedRef),
        ];
        
        if (user?.role === 'admin' || user?.role === 'employee') {
          promises.push(fetchUsers(signal, mountedRef));
        }
        if (user?.role === 'employee') {
          promises.push(fetchTasks(signal, mountedRef));
        }
        if (user?.role === 'admin') {
          promises.push(fetchPendingDocuments(signal, mountedRef));
        }
        
        // Wait for all promises, but don't fail if one fails
        await Promise.allSettled(promises);
      } catch (error) {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Error loading dashboard data:', error);
        }
      }
    };

    loadAllData();

    // ✅ Cleanup: annuler toutes les requêtes lors du démontage
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [user?.role, token]);

  const fetchStats = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
        timeout: 10000 // 10 second timeout
      });
      if (isMountedRef.current && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      // Only show error if it's not a cancellation
      if (error.name !== 'AbortError' && error.name !== 'CanceledError' && !axios.isCancel(error)) {
        console.error('Failed to fetch dashboard stats:', error);
        // Don't reset stats to 0 on error - keep existing values or leave as null
        // This prevents the "flashing 0" problem when requests are cancelled
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchMeetings = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/meetings`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setMeetings(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch meetings:', error);
      }
    }
  };

  const fetchUsers = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setUsers(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch users:', error);
      }
    }
  };

  const fetchFiles = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/files`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setFiles(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch files:', error);
      }
    }
  };

  const fetchTasks = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setTasks(response.data || []);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch tasks:', error);
      }
    }
  };

  const handleToggleTaskStatus = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'termine' ? 'en_cours' : 'termine';
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Statut de la tâche mis à jour');
      fetchTasks();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  useEffect(() => {
    filterFilesByStatus();
  }, [files, fileStatusTab]);

  const filterFilesByStatus = () => {
    const filtered = files.filter((file) => {
      const status = file.file_status || 'actif';
      return status === fileStatusTab;
    });
    setFilteredFiles(filtered);
  };


  const fetchPendingDocuments = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/documents/pending-approval`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setPendingDocsCount(response.data.length);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch pending documents:', error);
      }
    }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/meetings`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Meeting créé avec succès');
      setDialogOpen(false);
      resetForm();
      fetchMeetings();
    } catch (error) {
      toast.error('Erreur lors de la création du meeting');
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Supprimer ce meeting ?')) return;

    try {
      await axios.delete(`${API}/meetings/${meetingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Meeting supprimé');
      fetchMeetings();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdateStatus = async (meetingId, status) => {
    try {
      await axios.put(
        `${API}/meetings/${meetingId}/status`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { status }
        }
      );
      toast.success('Statut mis à jour');
      fetchMeetings();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      participant_ids: [],
      date: format(new Date(), 'yyyy-MM-dd'),
      time: '09:00',
      duration: 60,
      location: '',
      meeting_type: 'employee'
    });
  };

  const getMeetingsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return meetings.filter(m => m.date === dateStr);
  };

  const getUpcomingMeetings = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return meetings
      .filter(m => m.date >= today && m.status === 'scheduled')
      .sort((a, b) => {
        if (a.date === b.date) return a.time.localeCompare(b.time);
        return a.date.localeCompare(b.date);
      })
      .slice(0, 5);
  };

  const getStatusBadge = (status) => {
    const badges = {
      scheduled: <Badge className="bg-blue-100 text-blue-800">Programmé</Badge>,
      completed: <Badge className="bg-green-100 text-green-800">Terminé</Badge>,
      cancelled: <Badge className="bg-red-100 text-red-800">Annulé</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const statCards = [
    {
      title: t('total_files'),
      value: stats?.total_files || 0,
      icon: FolderOpen,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: t('active_files'),
      value: stats?.active_files || 0,
      icon: FileClock,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: t('closed_files'),
      value: stats?.closed_files || 0,
      icon: FileCheck,
      color: 'from-gray-500 to-gray-600',
      bgColor: 'bg-gray-50'
    },
    {
      title: t('pending_invoices'),
      value: stats?.pending_invoices || 0,
      icon: Receipt,
      color: 'from-yellow-500 to-yellow-600',
      bgColor: 'bg-yellow-50'
    }
  ];

  if (user?.role === 'admin') {
    statCards.push({
      title: t('total_employees'),
      value: stats?.total_employees || 0,
      icon: Users,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50'
    });
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-container">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              {t('welcome')}, {user?.full_name}
            </h1>
            <p className="text-lg text-gray-600">{t('dashboard')} - {t(user?.role)}</p>
          </div>
        </div>

        {/* Onglets pour Employé */}
        {user?.role === 'employee' && (
          <div className="flex space-x-2 bg-gradient-to-r from-orange-100 to-red-100 p-1.5 rounded-lg w-fit shadow-lg border-2 border-orange-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 ${
                activeTab === 'overview'
                  ? 'bg-white text-orange-600 shadow-md'
                  : 'text-gray-700 hover:bg-white/50'
              }`}
            >
              <Briefcase className="h-5 w-5" />
              Vue d'ensemble
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 ${
                activeTab === 'tasks'
                  ? 'bg-white text-orange-600 shadow-md'
                  : 'text-gray-700 hover:bg-white/50'
              }`}
            >
              <ListTodo className="h-5 w-5" />
              Tâches à faire
              {tasks.length > 0 && (
                <Badge className="bg-orange-600 text-white">
                  {tasks.filter(t => t.status !== 'termine').length}
                </Badge>
              )}
            </button>
          </div>
        )}

        {/* Contenu basé sur l'onglet actif pour employé */}
        {user?.role === 'employee' && activeTab === 'tasks' ? (
          <div className="space-y-6">
            <Card className="border-0 shadow-xl bg-gradient-to-br from-orange-50 to-red-50">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <ListTodo className="h-7 w-7 text-orange-600" />
                    Mes Tâches Assignées
                  </CardTitle>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-orange-600">
                      {tasks.filter(t => t.status !== 'termine').length}
                    </div>
                    <p className="text-sm text-gray-600">tâche(s) en cours</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {tasks.length > 0 ? (
                    tasks.map((task) => {
                      const isCompleted = task.status === 'termine';
                      const priorityColors = {
                        low: 'bg-blue-100 text-blue-800',
                        medium: 'bg-yellow-100 text-yellow-800',
                        high: 'bg-orange-100 text-orange-800',
                        urgent: 'bg-red-100 text-red-800'
                      };

                      return (
                        <Card key={task.id} className={`border-2 ${isCompleted ? 'border-green-500 bg-green-50' : 'border-orange-200 bg-white'}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              <input
                                type="checkbox"
                                checked={isCompleted}
                                onChange={() => handleToggleTaskStatus(task.id, task.status)}
                                className="w-5 h-5 text-green-600 rounded cursor-pointer mt-1"
                              />
                              <div className="flex-1">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <h4 className={`font-semibold ${isCompleted ? 'text-green-900 line-through' : 'text-gray-900'}`}>
                                      {task.title}
                                    </h4>
                                    <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                                  </div>
                                  <div className="flex gap-2 ml-4">
                                    <Badge className={priorityColors[task.priority]}>
                                      {task.priority === 'low' ? 'Faible' :
                                       task.priority === 'medium' ? 'Moyenne' :
                                       task.priority === 'high' ? 'Haute' : 'Urgente'}
                                    </Badge>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                  {task.deadline && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-4 w-4" />
                                      Échéance: {new Date(task.deadline).toLocaleDateString('fr-FR')}
                                    </span>
                                  )}
                                  <span>Assignée par: {task.assigned_by_name}</span>
                                  {task.file_id && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => navigate(`/files/${task.file_id}`)}
                                      className="ml-auto"
                                    >
                                      <FileText className="h-4 w-4 mr-1" />
                                      Voir le dossier
                                    </Button>
                                  )}
                                </div>

                                {isCompleted && task.completed_at && (
                                  <p className="text-sm text-green-600 font-medium mt-2">
                                    ✓ Complétée le {new Date(task.completed_at).toLocaleDateString('fr-FR')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  ) : (
                    <Card className="border-2 border-dashed border-orange-300 bg-white">
                      <CardContent className="p-8 text-center">
                        <ListTodo className="h-12 w-12 mx-auto mb-4 text-orange-400 opacity-50" />
                        <p className="text-gray-600">
                          Aucune tâche ne vous a encore été assignée.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
            {/* Vue d'ensemble normale */}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className={`border-0 shadow-xl hover:shadow-2xl transition-all duration-300 ${stat.bgColor}`}
                data-testid={`stat-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-lg bg-gradient-to-br ${stat.color}`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center text-sm text-green-600">
                    <TrendingUp className="h-4 w-4 mr-1" />
                    <span>{t('view_details')}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>


        {/* Dossiers Actifs/Clos Tabs Section */}
        {(user?.role === 'admin' || user?.role === 'employee') && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FolderOpen className="h-6 w-6 text-red-600" />
                Gestion des Dossiers
              </h2>
              <Button
                onClick={() => navigate('/files')}
                variant="outline"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                Voir tous les dossiers
                <Eye className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Tabs */}
            <div className="flex space-x-2 bg-gray-100 p-1.5 rounded-lg w-fit shadow-inner">
              <button
                onClick={() => setFileStatusTab('actif')}
                className={`px-6 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                  fileStatusTab === 'actif'
                    ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                data-testid="dashboard-actif-tab"
              >
                <span>📂 Dossiers Actifs</span>
                <Badge 
                  className={`${
                    fileStatusTab === 'actif'
                      ? 'bg-white/20 text-white border-white/30'
                      : 'bg-gray-200 text-gray-700 border-gray-300'
                  }`}
                >
                  {files.filter(f => (f.file_status || 'actif') === 'actif').length}
                </Badge>
              </button>
              <button
                onClick={() => setFileStatusTab('clos')}
                className={`px-6 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                  fileStatusTab === 'clos'
                    ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                data-testid="dashboard-clos-tab"
              >
                <span>🗄️ Dossiers Clos</span>
                <Badge 
                  className={`${
                    fileStatusTab === 'clos'
                      ? 'bg-white/20 text-white border-white/30'
                      : 'bg-gray-200 text-gray-700 border-gray-300'
                  }`}
                >
                  {files.filter(f => (f.file_status || 'actif') === 'clos').length}
                </Badge>
              </button>
            </div>

            {/* Files List */}
            {filteredFiles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredFiles.slice(0, 6).map((file) => (
                  <Card
                    key={file.id}
                    className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                    onClick={() => navigate(`/files/${file.id}`)}
                    data-testid={`dashboard-file-card-${file.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <h3 className="font-semibold text-gray-900 text-sm">
                            {file.file_number}
                          </h3>
                        </div>
                        <Badge className={`text-xs ${
                          file.file_status === 'actif' 
                            ? 'bg-green-100 text-green-700 border-green-200' 
                            : 'bg-gray-100 text-gray-700 border-gray-200'
                        }`}>
                          {file.file_status === 'actif' ? 'Actif' : 'Clos'}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center text-gray-600 text-sm">
                          <User className="h-3 w-3 mr-2" />
                          <span className="truncate">{file.client_name}</span>
                        </div>
                        <div className="flex items-center text-gray-500 text-xs">
                          <CalendarIcon className="h-3 w-3 mr-2" />
                          <span>
                            {format(new Date(file.created_at), 'dd/MM/yyyy')}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-3 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/files/${file.id}`);
                        }}
                      >
                        <Eye className="mr-1 h-3 w-3" />
                        Voir détails
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center text-gray-500">
                  <Archive className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-semibold mb-2">
                    {fileStatusTab === 'actif' ? '📂 Aucun dossier actif' : '🗄️ Aucun dossier clos'}
                  </p>
                  <p className="text-sm text-gray-400">
                    {fileStatusTab === 'actif' 
                      ? 'Les nouveaux dossiers apparaîtront ici' 
                      : 'Les dossiers clos apparaîtront ici'}
                  </p>
                </CardContent>
              </Card>
            )}

            {filteredFiles.length > 6 && (
              <div className="text-center">
                <Button
                  onClick={() => navigate('/files')}
                  variant="outline"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  Voir les {filteredFiles.length - 6} autres dossiers
                  <TrendingUp className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Documents Pending Approval - Admin Only */}
        {user?.role === 'admin' && pendingDocsCount > 0 && (
          <Card className="border-0 shadow-xl bg-gradient-to-br from-yellow-50 to-orange-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-yellow-600 to-orange-600">
                    <Clock className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {pendingDocsCount} Document{pendingDocsCount > 1 ? 's' : ''} en Attente d'Approbation
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Des documents générés nécessitent votre validation
                    </p>
                  </div>
                </div>
                <a href="/document-approvals">
                  <Button className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700">
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Voir les documents
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Outils de Gestion Intégrés - Admin & Employee */}
        {(user?.role === 'admin' || user?.role === 'employee') && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Briefcase className="h-6 w-6 text-red-600" />
              Outils de Gestion
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-green-50 to-blue-50 cursor-pointer"
                onClick={() => window.location.href = '/workflow-tracker'}>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-lg bg-gradient-to-br from-green-600 to-blue-600">
                        <TrendingUp className="h-8 w-8 text-white" />
                      </div>
                      <Badge className="bg-green-500 text-white">Intégré</Badge>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Suivi Workflow
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Suivi en temps réel de la progression de tous les dossiers clients avec visualisation par étapes
                      </p>
                    </div>
                    <Button className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700">
                      <TrendingUp className="mr-2 h-5 w-5" />
                      Accéder au suivi
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Agenda Section */}
        {(user?.role === 'admin' || user?.role === 'employee') && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Calendar */}
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon className="h-6 w-6 text-red-600" />
                    Agenda & Calendrier
                  </CardTitle>
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-gradient-to-r from-red-600 to-blue-600">
                        <Plus className="mr-2 h-5 w-5" />
                        Nouveau Meeting
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Créer un Meeting</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateMeeting} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Titre *</Label>
                          <Input
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Date *</Label>
                            <Input
                              type="date"
                              value={formData.date}
                              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Heure *</Label>
                            <Input
                              type="time"
                              value={formData.time}
                              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                              required
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Durée (minutes)</Label>
                            <Input
                              type="number"
                              value={formData.duration}
                              onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                              min="15"
                              step="15"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Type</Label>
                            <Select
                              value={formData.meeting_type}
                              onValueChange={(value) => {
                                // ✅ Reset participants when changing type to avoid removeChild error
                                setFormData({ 
                                  ...formData, 
                                  meeting_type: value,
                                  participant_ids: [] 
                                });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="employee">Employé</SelectItem>
                                <SelectItem value="client">Client</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Lieu</Label>
                          <Input
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                            placeholder="Bureau, Zoom, etc."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Participants *</Label>
                          <Select
                            key={`participants-${formData.meeting_type}`}
                            value=""
                            onValueChange={(value) => {
                              if (value && !formData.participant_ids.includes(value)) {
                                setFormData({ ...formData, participant_ids: [...formData.participant_ids, value] });
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Ajouter des participants" />
                            </SelectTrigger>
                            <SelectContent>
                              {users
                                .filter(u => formData.meeting_type === 'employee' ? u.role === 'employee' : u.role === 'client')
                                .filter(u => !formData.participant_ids.includes(u.id))
                                .map(u => (
                                  <SelectItem key={u.id} value={u.id}>
                                    {u.full_name} ({u.email})
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {formData.participant_ids.map(id => {
                              const participant = users.find(u => u.id === id);
                              return participant ? (
                                <Badge key={id} className="flex items-center gap-1">
                                  {participant.full_name}
                                  <button
                                    type="button"
                                    onClick={() => setFormData({
                                      ...formData,
                                      participant_ids: formData.participant_ids.filter(pid => pid !== id)
                                    })}
                                    className="ml-1"
                                  >
                                    ×
                                  </button>
                                </Badge>
                              ) : null;
                            })}
                          </div>
                        </div>
                        <Button type="submit" className="w-full bg-gradient-to-r from-red-600 to-blue-600">
                          Créer le Meeting
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <Calendar
                  onChange={setSelectedDate}
                  value={selectedDate}
                  locale="fr-FR"
                  className="rounded-lg border-0"
                  tileContent={({ date }) => {
                    const dayMeetings = getMeetingsForDate(date);
                    return dayMeetings.length > 0 ? (
                      <div className="flex justify-center mt-1">
                        <div className="h-1.5 w-1.5 bg-red-600 rounded-full"></div>
                      </div>
                    ) : null;
                  }}
                />
                {selectedDate && (
                  <div className="mt-4">
                    <h4 className="font-semibold mb-2">
                      Meetings du {format(selectedDate, 'dd MMMM yyyy', { locale: fr })}
                    </h4>
                    {getMeetingsForDate(selectedDate).length > 0 ? (
                      <div className="space-y-2">
                        {getMeetingsForDate(selectedDate).map(meeting => (
                          <div key={meeting.id} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium">{meeting.title}</p>
                                <p className="text-sm text-gray-600">
                                  <Clock className="inline h-3 w-3 mr-1" />
                                  {meeting.time} ({meeting.duration}min)
                                </p>
                              </div>
                              {getStatusBadge(meeting.status)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Aucun meeting</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Meetings */}
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle>Meetings à Venir</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {getUpcomingMeetings().length > 0 ? (
                    getUpcomingMeetings().map(meeting => (
                      <Card key={meeting.id} className="border">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-semibold">{meeting.title}</h4>
                              <p className="text-sm text-gray-600">{meeting.description}</p>
                            </div>
                            {getStatusBadge(meeting.status)}
                          </div>
                          <div className="space-y-1 text-sm text-gray-600 mb-3">
                            <p>
                              <CalendarIcon className="inline h-4 w-4 mr-1" />
                              {format(parseISO(meeting.date), 'dd/MM/yyyy')} à {meeting.time}
                            </p>
                            {meeting.location && (
                              <p>
                                <MapPin className="inline h-4 w-4 mr-1" />
                                {meeting.location}
                              </p>
                            )}
                            <p className="text-xs">
                              Organisateur: {meeting.organizer_name}
                            </p>
                            <p className="text-xs">
                              Participants: {meeting.participant_names.join(', ')}
                            </p>
                          </div>
                          {(meeting.organizer_id === user?.id || user?.role === 'admin') && (
                            <div className="flex gap-2">
                              {meeting.status === 'scheduled' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUpdateStatus(meeting.id, 'completed')}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Terminé
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUpdateStatus(meeting.id, 'cancelled')}
                                  >
                                    <XCircle className="h-4 w-4 mr-1" />
                                    Annuler
                                  </Button>
                                </>
                              )}
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteMeeting(meeting.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-8">Aucun meeting à venir</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Statistiques Section */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle>Statistiques</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-gray-500">
              <p>Les graphiques et activités récentes seront affichés ici</p>
            </div>
          </CardContent>
        </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
